import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { CellClassParams, CellClickedEvent, SuppressKeyboardEventParams } from 'ag-grid-community';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, Subscription } from 'rxjs';

import { FormlyFieldBaseComponent } from '../formly-field-base.component';
import { AggFunc } from '../../../constants';
import { WarpWeftPlan, WarpWeftPlanRepeat } from '../../../models';
import { ModalService, QualityService, UtilityService } from '../../../services';
import { GridService } from '../../../services/component-services/grid.service';
import { FormlyService } from '../../../services/component-services/formly.service';
import { BaseInlineFormComponent } from '../../base-inline-form/base-inline-form.component';
import { BaseFormComponent } from '../../base-form/base-form.component';
import { ignoreModalError } from '../../../helpers';
import { GridComponent } from '../../grids/grid/grid.component';
import { FormOkCancelModalComponent } from '../../modals/form-ok-cancel-modal/form-ok-cancel-modal.component';

// Config-driven plan grid (weaving WS5). Defaults preserve the original warp
// behaviour; draft/peg/weft plans configure via the plan* props:
//   planRows (12), planRowLabels 'letters'|'numbers', planShowCalcColumns,
//   planShowCode, planPatchTotals, planWatchQualitySelected, planMaxColumns (500 ceiling),
//   planRowValue + planRowValueHeader (an extra right-hand column, summed at the bottom).
// Columns are built dynamically: start small, grow with the plan up to the ceiling.
@Component({
  selector: 'app-formly-warp-weft-plan',
  templateUrl: './formly-warp-weft-plan.component.html',
  styleUrl: './formly-warp-weft-plan.component.scss',
  imports: [BaseInlineFormComponent, GridComponent, BaseFormComponent, AsyncPipe],
})
export class FormlyWarpWeftPlanComponent extends FormlyFieldBaseComponent implements OnInit, OnDestroy {
  private readonly gridService = inject(GridService);
  private readonly formlyService = inject(FormlyService);
  private readonly modalService = inject(ModalService);
  private readonly utilityService = inject(UtilityService);
  private readonly qualityService = inject(QualityService);

  private readonly grid = viewChild(GridComponent);

  private static readonly initialColumns = 50;
  protected columnCount = FormlyWarpWeftPlanComponent.initialColumns;

  rowData$ = new BehaviorSubject<any[]>([]);

  protected columnDefs: ColDef[] = [];

  private addBracketFields: FormlyFieldConfig[] = [
    this.formlyService.number('repeat', 'Repeat Count', {
      required: true,
      min: 2,
    }),
  ];

  protected loaded = false;
  private subscription: Subscription;
  protected buttons: FormlyFieldConfig[];
  protected fields: FormlyFieldConfig[];
  protected fieldModel: any = {};

  // --- plan* prop accessors (defaults = original warp behaviour) ---

  // Warp/weft plans have a fixed number of shade rows. Draft/peg plans size
  // themselves from fields the user enters (Height/Width/No of Extras), so those
  // read from the model and the grid is rebuilt whenever they change.
  private get rowCount(): number {
    return this.markRowCount + this.extrasRowCount;
  }

  // Rows that hold plan marks - below these sit the extras rows (draft only).
  private get markRowCount(): number {
    return this.props.planRowsField ? +this.model[this.props.planRowsField] || 0 : (this.props.planRows ?? 12);
  }

  private get extrasCount(): number {
    return this.props.planExtrasField ? +this.model[this.props.planExtrasField] || 0 : 0;
  }

  // A draft plan's extras are extra rows; a peg plan's are its first columns.
  private get extrasRowCount(): number {
    return this.props.planExtrasPlacement === 'rows' ? this.extrasCount : 0;
  }

  private get extrasColumnCount(): number {
    return this.props.planExtrasPlacement === 'columns' ? this.extrasCount : 0;
  }

  private get markerMode(): boolean {
    return this.props.planCellMode === 'marker';
  }

  // Draft cells cycle X -> O -> blank, peg cells X -> blank.
  private get markerCycle(): string[] {
    return this.props.planMarkerCycle ?? ['X'];
  }

  protected get coordinateCode(): boolean {
    return this.props.planCodeFormat === 'coordinate';
  }

  // The legend under the plan (R4): how many shade rows are in play and what the
  // bracket rows mean. Lettered plans only - a draft's rows are shafts, not shades.
  protected get shadeSummary(): string {
    return this.lettersMode ? `${this.markRowCount} (A–${this.rowLabel(this.markRowCount)})` : '';
  }

  private get lettersMode(): boolean {
    return (this.props.planRowLabels ?? 'letters') === 'letters';
  }

  private get showCalcColumns(): boolean {
    return this.props.planShowCalcColumns ?? true;
  }

  protected get showCode(): boolean {
    // Lettered plans use the bracket code (3(2A1B)); draft/peg use the
    // coordinate code (1X1YX). A numbered plan without a code format has neither.
    return this.props.planShowCode ?? (this.lettersMode || this.coordinateCode);
  }

  private get patchTotals(): boolean {
    return this.props.planPatchTotals ?? true;
  }

  private get watchQualitySelected(): boolean {
    return this.props.planWatchQualitySelected ?? true;
  }

  private get columnCeiling(): number {
    return this.props.planMaxColumns ?? 500;
  }

  // Draft/peg (Width) and sley (fixed 200) declare their own extent.
  private get fixedColumns(): boolean {
    return !!(this.props.planColumnsField || this.props.planColumns);
  }

  // The field the code is read from and written back to. The sley plan has no
  // stored rows, so its code IS the record - it goes straight onto the model.
  private get codeField(): string {
    return this.props.planCodeField ?? 'code';
  }

  ngOnInit(): void {
    this.reset();

    this.columnCount = this.initialColumnCount();
    this.buildColumnDefs();
    this.rowData$.next(this.buildRowData());

    this.rowData$.pipe(this.untilDestroyed()).subscribe((rowData) => this.grid()?.gridApi.setRowData(rowData));

    // A draft/peg plan is sized by the fields the user types, so rebuild the grid
    // whenever Height / Width / No of Extras change.
    if (this.props.planRowsField || this.props.planColumnsField || this.props.planExtrasField) {
      this.form.valueChanges.pipe(this.untilDestroyed()).subscribe(() => this.resizeToFields());
    }

    if (this.watchQualitySelected) {
      this.qualityService.qualitySelected$.pipe(this.untilDestroyed()).subscribe((id) => this.updateQuality(id));
    }

    this.subscription = this.form.valueChanges.pipe(this.untilDestroyed()).subscribe((model) => {
      if (this.loaded) {
        return;
      }
      // A code-only plan (sley) has no stored rows to wait for - it draws itself
      // from the code as soon as the record arrives.
      if (this.props.planCodeField) {
        if (model[this.codeField] === undefined) {
          return;
        }
        this.loaded = true;
        this.clearSubscription();
        this.fieldModel.code = model[this.codeField];
        this.codeToGrid(model[this.codeField]);
        return;
      }
      if (model[this.key] && model[this.planRepeatsProperty]) {
        this.loaded = true;
        this.clearSubscription();
        this.fieldModel.code = this.model.code;
        this.populatePlans(model[this.key]);
        this.populatePlanRepeats(model[this.planRepeatsProperty]);
        this.gridToCode();
      }
    });

    this.getFields();
  }

  ngOnDestroy() {
    this.clearSubscription();
  }

  // Lettered plans (warp/weft) read A..L down the screen. Numbered plans
  // (draft/peg) run Height..1 instead, putting shaft 1 at the bottom as it is on
  // the loom. Then come the extras rows (draft only, labelled A, B, C...) and the
  // two bracket rows. Rows keep their stored number - only the display order flips.
  private buildRowData(): any[] {
    const rows: any[] = [];
    for (let i = 1; i <= this.markRowCount; i++) {
      const row = this.lettersMode ? i : this.markRowCount - i + 1;
      rows.push({ letter: this.rowLabel(row), row });
    }
    for (let extra = 1; extra <= this.extrasRowCount; extra++) {
      const row = this.markRowCount + extra;
      rows.push({ letter: this.rowLabel(row), row, extraRow: true });
    }
    // The two bracket rows, labelled so the levels read at a glance (R4, 28 Aug notes:
    // "add label for level 1 brackets and level 2 brackets"). Coordinate plans (draft/
    // peg) keep unlabelled placeholders - they carry no brackets today.
    return this.coordinateCode
      ? [...rows, {}, {}]
      : [...rows, { letter: $localize`L1`, repeatLevel: 1 }, { letter: $localize`L2`, repeatLevel: 2 }];
  }

  // Columns come from a field (draft/peg width), a fixed count (sley), or the
  // default start-small-and-grow behaviour (warp/weft) - always inside the ceiling.
  // A peg plan's extras are columns ADDED to the left of Width, as in TextileMan,
  // so the grid runs Width + No of Extras wide.
  private initialColumnCount(): number {
    if (this.props.planColumnsField) {
      const width = +this.model[this.props.planColumnsField] || 0;
      return Math.min(width + this.extrasColumnCount, this.columnCeiling);
    }
    return Math.min(this.props.planColumns ?? FormlyWarpWeftPlanComponent.initialColumns, this.columnCeiling);
  }

  // The user changed Height / Width / No of Extras - resize and redraw, keeping
  // any marks that still fit inside the new extent.
  private resizeToFields() {
    const columns = this.props.planColumnsField ? this.initialColumnCount() : this.columnCount;
    if (columns === this.columnCount && this.rowData$.value.length === this.rowCount + 2) {
      return;
    }
    this.columnCount = columns;
    this.buildColumnDefs();

    this.grid()?.gridApi?.setColumnDefs(this.columnDefs);

    // Build the new grid contents in one go - a fresh row set with the surviving
    // marks written into it - so the grid is never handed a half-resized state.
    const data = this.buildRowData();
    this.fillPlans(data, this.plan ?? []);
    this.rowData$.next(data);
  }

  // A peg plan's extras are the leading columns and are left unnumbered, so the
  // user's "column 1" is the first real column after them - TextileMan does the
  // same. Everywhere else the column IS its number.
  columnLabel(column: number): string {
    if (column <= this.extrasColumnCount) {
      return '';
    }
    return (column - this.extrasColumnCount).toString();
  }

  // Mark rows are labelled by the plan's own scheme (letters for warp/weft,
  // numbers for draft/peg); the extras rows below them are always A, B, C...
  rowLabel(row: number): string {
    if (row > this.markRowCount) {
      return this.getRowLetter(row - this.markRowCount);
    }
    return this.lettersMode ? this.getRowLetter(row) : row.toString();
  }

  // The inverse of rowLabel - 'A' is an extras row on a numbered plan.
  private labelRow(label: string): number {
    if (label >= 'A' && label <= 'Z') {
      const index = this.getLetterRow(label);
      return this.lettersMode ? index : this.markRowCount + index;
    }
    return +label;
  }

  getRowLetter(row: number): string {
    return String.fromCharCode(row + 64);
  }

  getLetterRow(letter: string): number {
    return letter.charCodeAt(0) - 64;
  }

  // Draft/peg cells hold marks, stored as the numbers TextileMan uses:
  // X = 9, O = 8, E = 7. Any other number is kept and shown as itself.
  private static readonly markerNumbers: Record<string, number> = { X: 9, O: 8, E: 7 };

  private markerToNumber(value: string): number {
    const marker = (value ?? '').toString().toUpperCase();
    return (FormlyWarpWeftPlanComponent.markerNumbers[marker] ?? +marker) || 0;
  }

  private numberToMarker(value: number): string {
    const marker = Object.keys(FormlyWarpWeftPlanComponent.markerNumbers).find(
      (key) => FormlyWarpWeftPlanComponent.markerNumbers[key] === value,
    );
    return marker ?? value.toString();
  }

  edit(model: any) {
    // model format: { letter: 'A', row: 1, 1: '4', 2: '3' }
    let plan = this.plan;
    const cells: WarpWeftPlan[] = [];
    const row = model.row;
    for (const prop in model) {
      switch (prop) {
        case 'letter':
        case 'row':
        case 'repeatLevel':
          break;
        default:
          const cell: WarpWeftPlan = {
            row,
          } as WarpWeftPlan;

          cell.column = +prop;

          const value = model[prop];
          if (value === '*') {
            cell.extra = value;
          } else if (this.markerMode) {
            cell.number = this.markerToNumber(value);
          } else {
            cell.number = +value;
          }

          cells.push(cell);
          break;
      }
    }
    for (const cell of cells) {
      let found = false;
      for (const p of plan) {
        if (p.column === cell.column && p.row === cell.row) {
          if (cell.number || cell.extra) {
            p.number = cell.number;
            p.extra = cell.extra;
          } else {
            plan = plan.filter((x) => x.row !== cell.row || x.column !== cell.column);
          }
          found = true;
          break;
        }
      }
      if (!found) {
        plan.push(cell);
      }
    }

    if (this.props.planSingleMarkPerColumn) {
      plan = this.enforceSingleMarkPerColumn(plan, cells);
    }

    plan = this.utilityService.sort(
      plan,
      (x) => x.column,
      (x) => x.row,
    );
    this.form.patchValue({
      [this.key]: plan,
    } as any);
    // Don't repopulate the grid here - AG-Grid already holds the edited value, and
    // a full setRowData on every keystroke is what made large plans feel slow.
    // enforceSingleMarkPerColumn redraws only when it actually clears a cell.
    this.ensureColumns(Math.max(0, ...plan.map((x) => x.column)));
    this.calculate();
    this.gridToCode();
    this.setEdited();
  }

  // A dent holds either 1 or 2 ends, never both, so marking a cell clears any
  // other mark in that column (TextileMan does the same on the sley and draft
  // plans). Extras rows are exempt - they flag the column, they aren't a route
  // through it.
  private enforceSingleMarkPerColumn(plan: WarpWeftPlan[], edited: WarpWeftPlan[]): WarpWeftPlan[] {
    const data = this.rowData$.value;
    const clashes: WarpWeftPlan[] = [];
    for (const cell of edited) {
      if (!cell.number || cell.row > this.markRowCount) {
        continue;
      }
      for (const other of plan) {
        if (other.column === cell.column && other.row !== cell.row && other.row <= this.markRowCount) {
          clashes.push(other);
          const row = data.find((x) => x.row === other.row);
          if (row) {
            row[other.column] = '';
          }
        }
      }
    }
    if (!clashes.length) {
      return plan;
    }
    this.rowData$.next(data);
    return plan.filter((x) => !clashes.includes(x));
  }

  private get planRepeatsProperty() {
    return this.key + 'Repeats';
  }

  private get plan(): WarpWeftPlan[] {
    return this.model[this.key] as WarpWeftPlan[];
  }

  private get planRepeats(): WarpWeftPlanRepeat[] {
    return this.model[this.planRepeatsProperty] as WarpWeftPlanRepeat[];
  }

  private buildColumnDefs() {
    const defs: ColDef[] = [];
    if (this.showCalcColumns) {
      defs.push(
        this.gridService.calculation('threads', $localize`Threads`, (model) => this.countThreads(model), {
          aggFunc: AggFunc.sumInteger,
          width: 70,
          pinned: 'left',
        }),
        this.gridService.calculation('extras', $localize`Extras`, (model) => this.countExtras(model), {
          aggFunc: AggFunc.sumInteger,
          width: 70,
          pinned: 'left',
        }),
      );
    }
    // An optional read-only value per row, pinned RIGHT and summed into the bottom
    // totals row (the selvedge plan's yarn weight). Off unless the caller supplies
    // planRowValue, so every other plan is unaffected.
    if (this.props.planRowValue) {
      defs.push(
        this.gridService.calculation(
          'rowValue',
          this.props.planRowValueHeader ?? '',
          (model) => this.props.planRowValue(model.row),
          { aggFunc: AggFunc.sum, width: 90, pinned: 'right' },
        ),
      );
    }

    defs.push(
      this.gridService.text('letter', '', { width: 35, cellClass: 'fw-bold', pinned: 'left' }),
      ...this.utilityService.array1ToN(this.columnCount).map((index) =>
        this.gridService.editableField(
          index.toString(),
          this.columnLabel(index),
          (model) => this.edit(model),
          (model) => !this.props.editable || this.props.editable(this.model, model.row),
          {
            width: 50,
            // Marker cells are cycled by clicking (as in TextileMan) as well as typed.
            onCellClicked: this.markerMode ? (params: CellClickedEvent) => this.cycleMarker(params) : undefined,
            // Keyboard: SPACE cycles a focused marker cell (R4, 28 Aug notes: "make space =
            // black square, space again means turn square off"), and the arrow keys move
            // between cells even while one is being typed in - see planKeyboard.
            suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => this.planKeyboard(params),
          },
          (cellClassParams: CellClassParams) => this.getCellColour(cellClassParams),
          this.markerMode
            ? /(^[XOExoe]$)|(^$)/ // draft/peg cells hold a mark, not a thread count
            : /(^[0-9]+$)|(^\*$)|(^$)/, // only allow numbers, '*' or empty string
        ),
      ),
    );
    this.columnDefs = defs;
  }

  // Clicking a marker cell steps through the plan's cycle and back to blank:
  // a draft goes X -> O -> blank, a peg X -> blank. Extras cells hold 'E' only.
  private cycleMarker(params: CellClickedEvent) {
    this.cycleMarkerCell(params.colDef.field, params.node, params.data);
  }

  // Every keystroke on a plan cell comes through here. Returns true to swallow it.
  private planKeyboard(params: SuppressKeyboardEventParams): boolean {
    if (this.markerMode && this.markerKeyboard(params)) {
      return true;
    }
    return this.arrowKeyWhileEditing(params);
  }

  // The keyboard twin of the click: SPACE cycles the focused square. Returns true to
  // swallow the keystroke, so the grid does not also treat space as row selection.
  private markerKeyboard(params: SuppressKeyboardEventParams): boolean {
    if (params.event.key !== ' ' || params.editing) {
      return false;
    }

    this.cycleMarkerCell(params.column.getColDef().field, params.node, params.node?.data);
    return true;
  }

  // A plan is keyed like a spreadsheet. When no cell is being edited AG-Grid already moves
  // the focus with the arrows and typing starts the edit, so nothing is needed. While a cell
  // IS being typed in the default editor takes the arrows as caret movement, which sends the
  // user back to the mouse for every cell - so here Up / Down always commit and move, and
  // Left / Right commit and move once the caret is at the start / end of the value (in the
  // middle they still move the caret, so a number can be corrected). At the edge of the plan
  // nothing moves and the value is kept; Tab and Enter are left to the grid.
  private arrowKeyWhileEditing(params: SuppressKeyboardEventParams): boolean {
    if (!params.editing) {
      return false;
    }
    switch (params.event.key) {
      case 'ArrowUp':
        return this.commitAndMove(params, -1, 0);
      case 'ArrowDown':
        return this.commitAndMove(params, 1, 0);
      case 'ArrowLeft':
        return this.caretAt(params.event, 'start') ? this.commitAndMove(params, 0, -1) : false;
      case 'ArrowRight':
        return this.caretAt(params.event, 'end') ? this.commitAndMove(params, 0, 1) : false;
      default:
        return false;
    }
  }

  private caretAt(event: KeyboardEvent, edge: 'start' | 'end'): boolean {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return false;
    }
    const position = edge === 'start' ? 0 : input.value.length;
    return input.selectionStart === position && input.selectionEnd === position;
  }

  // Commits the edit and focuses the nearest editable plan cell in that direction. Swallows
  // the key either way: at the edge the editor's own handling (caret to start / end of the
  // value on Up / Down in Chrome) would otherwise disturb what the user typed.
  private commitAndMove(params: SuppressKeyboardEventParams, rowDelta: number, columnDelta: number): boolean {
    const rowIndex = this.targetRowIndex(params, rowDelta);
    const column = this.targetColumn(params, columnDelta);
    if (rowIndex === undefined || !column) {
      return true;
    }
    params.api.stopEditing();
    params.api.setFocusedCell(rowIndex, column);
    return true;
  }

  private targetRowIndex(params: SuppressKeyboardEventParams, rowDelta: number): number | undefined {
    const from = params.node?.rowIndex;
    if (rowDelta === 0) {
      return from;
    }
    const count = params.api.getDisplayedRowCount();
    for (let index = from + rowDelta; index >= 0 && index < count; index += rowDelta) {
      if (this.cellEditable(params.api.getDisplayedRowAtIndex(index)?.data)) {
        return index;
      }
    }
    return undefined;
  }

  private targetColumn(params: SuppressKeyboardEventParams, columnDelta: number) {
    let column = params.column;
    if (columnDelta === 0) {
      return column;
    }
    for (;;) {
      column =
        columnDelta > 0
          ? params.columnApi.getDisplayedColAfter(column)
          : params.columnApi.getDisplayedColBefore(column);
      if (!column) {
        return undefined;
      }
      // Plan columns are the numbered ones; the pinned letter / calculation columns are not.
      if (/^[0-9]+$/.test(column.getColDef().field ?? '') && this.cellEditable(params.node?.data)) {
        return column;
      }
    }
  }

  // The same rule editableField applies to a plan cell: not a totals row, and the plan's own
  // editable callback (bracket and code rows) says yes.
  private cellEditable(data: any): boolean {
    return (
      !!data &&
      !data.isAggregate &&
      !data.repeatLevel &&
      (!this.props.editable || this.props.editable(this.model, data.row))
    );
  }

  private cycleMarkerCell(field: string, node: { setDataValue(field: string, value: any): void }, data: any) {
    if (data?.isAggregate || data?.repeatLevel || !field) {
      return;
    }
    const column = +field;
    const isExtrasCell = data.extraRow || (this.extrasColumnCount > 0 && column <= this.extrasColumnCount);
    const cycle = isExtrasCell ? ['E'] : this.markerCycle;

    const current = (data[field] ?? '').toString().toUpperCase();
    const next = cycle[cycle.indexOf(current) + 1] ?? '';

    node.setDataValue(field, next);
    this.edit(data);
  }

  // Grow the grid to fit the plan (plus working room), up to the ceiling.
  private ensureColumns(needed: number) {
    // A draft/peg/sley plan's extent IS its Width (or a fixed count) - it must
    // never grow past it. Only warp/weft grids start small and grow as typed.
    if (this.fixedColumns || !needed || needed <= this.columnCount - 5) {
      return;
    }
    const target = Math.min(this.columnCeiling, Math.max(needed + 10, this.columnCount));
    if (target > this.columnCount) {
      this.columnCount = target;
      this.buildColumnDefs();
      this.grid()?.gridApi?.setColumnDefs(this.columnDefs);
    }
  }

  private getCellColour(params: CellClassParams) {
    if (params.data.isAggregate) {
      return undefined;
    }
    // A code-only plan (sley) has no repeats array on its model at all, so this
    // must tolerate the property being absent - it runs on every cell render.
    const planRepeats = this.planRepeats ?? [];
    const columnNumber = +params.column.getId();
    const level1s = planRepeats.filter((x) => x.level === 1);
    const level2s = planRepeats.filter((x) => x.level === 2);
    const level1 = level1s.find((x) => columnNumber >= x.startColumn && columnNumber <= x.endColumn);
    const level2 = level2s.find((x) => columnNumber >= x.startColumn && columnNumber <= x.endColumn);
    if (level2 && params.rowIndex === this.rowData$.value.length - 1) {
      return { backgroundColor: level2s.indexOf(level2) % 2 === 0 ? 'orange' : 'lightblue' };
    }
    if (level1) {
      return { backgroundColor: level1s.indexOf(level1) % 2 === 0 ? 'yellow' : 'lightgreen' };
    }
    return undefined;
  }

  private setEdited() {
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    for (const prop in this.form.controls) {
      this.form.controls[prop].updateValueAndValidity();
    }
    this.calculate();
  }

  private reset() {
    if (this.key && !this.model[this.key]) {
      this.form.patchValue({ [this.key]: undefined } as any);
    }
  }

  private getFields() {
    // The coordinate code carries no repeats, so a bracket on a draft/peg/sley
    // plan would be silently dropped - offer Clear Plan only, as TextileMan does.
    this.buttons = this.coordinateCode
      ? [this.formlyService.button($localize`Clear Plan`, () => this.clearPlan())]
      : [
          this.formlyService.button($localize`Add Bracket`, () => this.addBracket()),
          this.formlyService.button($localize`Remove Bracket`, () => this.removeBracket()),
          this.formlyService.button($localize`Clear Brackets`, () => this.clearBrackets()),
          this.formlyService.button($localize`Clear Plan`, () => this.clearPlan()),
        ];
    this.fields = this.showCode
      ? [
          this.formlyService.text('code', $localize`Code`, {
            change: (field: FormlyFieldConfig, event: any) => this.codeToGrid(event.target.value),
          }),
        ]
      : [];
  }

  private clearSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  private updateQuality(id: string) {
    this.qualityService
      .edit(id)
      .pipe(this.untilDestroyed())
      .subscribe((quality) => {
        this.form.patchValue({
          groundEnds: quality.groundEnds,
        } as any);
        this.calculate();
      });
  }

  private addBracket() {
    const selectedCells = this.grid().gridApi.getCellRanges()[0];
    if (!selectedCells?.columns) {
      return;
    }
    const startColumn = +selectedCells.columns[0]['colId'];
    const endColumn = +selectedCells.columns[selectedCells.columns.length - 1]['colId'];
    const planRepeats = this.planRepeats;
    const { modal, component } = this.modalService.openSmall<FormOkCancelModalComponent>(FormOkCancelModalComponent);
    component.header.set($localize`Add Bracket`);
    component.fields.set(this.addBracketFields);
    modal.result.then((model) => {
      const existingRepeats = planRepeats.filter((x) => x.startColumn >= startColumn && x.endColumn <= endColumn);
      if (existingRepeats.length === 0 || existingRepeats.filter((x) => x.level !== 1).length === 0) {
        planRepeats.push({
          startColumn,
          endColumn,
          level: existingRepeats.length ? 2 : 1,
          repeat: +model.repeat,
        });

        this.form.patchValue({
          [this.planRepeatsProperty]: planRepeats,
        } as any);
        this.populatePlanRepeats(planRepeats);
        this.gridToCode();
        this.setEdited();
      }
    }, ignoreModalError);
  }

  private removeBracket() {
    const selectedCells = this.grid().gridApi.getCellRanges()[0];
    if (!selectedCells?.columns) {
      return;
    }
    const startColumn = +selectedCells.columns[0]['colId'];
    const endColumn = +selectedCells.columns[selectedCells.columns.length - 1]['colId'];
    let planRepeats = this.planRepeats;
    planRepeats = planRepeats.filter((x) => !(x.startColumn >= startColumn && x.endColumn <= endColumn));
    this.form.patchValue({
      [this.planRepeatsProperty]: planRepeats,
    } as any);
    this.populatePlanRepeats(planRepeats);
    this.gridToCode();
    this.setEdited();
  }

  private clearBrackets() {
    const planRepeats = [];
    this.form.patchValue({
      [this.planRepeatsProperty]: planRepeats,
    } as any);
    this.populatePlanRepeats(planRepeats);
    this.gridToCode();
    this.setEdited();
  }

  private clearPlan() {
    this.clearGrid();

    const plans = [];
    const planRepeats = [];
    this.form.patchValue({
      [this.key]: plans,
      [this.planRepeatsProperty]: planRepeats,
    } as any);
    this.populatePlans(plans);
    this.populatePlanRepeats(planRepeats);
    this.gridToCode();
    this.setEdited();
  }

  clearGrid() {
    const data = this.rowData$.value.map((row) => ({
      row: row.row,
      letter: row.letter,
      repeatLevel: row.repeatLevel,
    }));
    this.rowData$.next(data);
  }

  private populatePlans(plans: WarpWeftPlan[]) {
    this.ensureColumns(Math.max(0, ...plans.map((x) => x.column)));
    const data = this.rowData$.value;
    this.fillPlans(data, plans);
    this.rowData$.next(data);
    this.calculate();
  }

  // Writes marks into a row set. Kept separate from populatePlans so a resize can
  // fill a freshly-built row set before it ever reaches the grid.
  private fillPlans(data: any[], plans: WarpWeftPlan[]) {
    for (const plan of plans) {
      const row = data.find((x) => x.row === plan.row);
      if (row) {
        row[plan.column] = plan.extra ?? (this.markerMode ? this.numberToMarker(plan.number) : plan.number.toString());
      }
    }
  }

  private populatePlanRepeats(planRepeats: WarpWeftPlanRepeat[]) {
    this.ensureColumns(Math.max(0, ...planRepeats.map((x) => x.endColumn)));
    const data = this.rowData$.value;
    data[data.length - 1] = { letter: $localize`L2`, repeatLevel: 2 };
    data[data.length - 2] = { letter: $localize`L1`, repeatLevel: 1 };
    for (const value of planRepeats) {
      const planRepeat = value as WarpWeftPlanRepeat;
      const row = data.find((x) => x.repeatLevel === planRepeat.level);
      if (row) {
        row[planRepeat.startColumn] = planRepeat.repeat;
      }
    }
    this.rowData$.next(data);
    this.calculate();
  }

  private calculate() {
    if (!this.patchTotals) {
      return;
    }
    const { endsPerRepeat, extrasPerRepeat } = this.countEndsAndExtras();
    const totalEndsPlusExtras = endsPerRepeat + extrasPerRepeat;
    const patternRepeats = totalEndsPlusExtras === 0 ? 0 : Math.floor(this.model.groundEnds / totalEndsPlusExtras);
    const endsOver = totalEndsPlusExtras === 0 ? 0 : this.model.groundEnds % totalEndsPlusExtras;

    let model: any = {
      endsPerRepeat,
      extrasPerRepeat,
      totalEndsPlusExtras,
      patternRepeats,
      endsOver,
    };

    if (!this.model.expectedEndsPlusExtras) {
      model = { ...model, expectedEndsPlusExtras: totalEndsPlusExtras };
    }

    this.form.patchValue(model);
  }

  private countThreads(model: any) {
    if (!model.letter) {
      return undefined;
    }
    const { endsPerRepeat } = this.countEndsAndExtras(model.row);
    return endsPerRepeat;
  }

  private countExtras(model: any) {
    if (!model.letter) {
      return undefined;
    }
    const { extrasPerRepeat } = this.countEndsAndExtras(model.row);
    return extrasPerRepeat;
  }

  countEndsAndExtras(row?: number) {
    // Both can be absent on a plan that stores neither (see getCellColour).
    const plan = this.plan ?? [];
    const planRepeats = this.planRepeats ?? [];
    let endsPerRepeat = 0;
    let extrasPerRepeat = 0;
    for (const cell of plan) {
      if (row && cell.row !== row) {
        continue;
      }
      let multiplier = 1;
      const repeats = planRepeats.filter((p) => cell.column >= p.startColumn && cell.column <= p.endColumn);
      for (const repeat of repeats) {
        multiplier *= repeat.repeat;
      }
      if (cell.number) {
        endsPerRepeat += cell.number * multiplier;
      }
      if (cell.extra) {
        extrasPerRepeat += multiplier;
      }
    }
    return { endsPerRepeat, extrasPerRepeat };
  }

  // Warp/weft plans write the bracket code, e.g. 4A3(2A1B) - <count><ROW LETTER>
  // pairs, where a bracket repeats the columns it wraps.
  private gridToCode() {
    if (!this.showCode) {
      return;
    }
    if (this.coordinateCode) {
      this.setCode(this.gridToCoordinateCode());
      return;
    }
    const tokens: string[] = [];

    for (let column = 1; column <= this.columnCount; column++) {
      const repeatLevel1Start = this.planRepeats.find((x) => x.startColumn === column && x.level === 1);
      const repeatLevel1End = this.planRepeats.find((x) => x.endColumn === column && x.level === 1);
      const repeatLevel2Start = this.planRepeats.find((x) => x.startColumn === column && x.level === 2);
      const repeatLevel2End = this.planRepeats.find((x) => x.endColumn === column && x.level === 2);
      const columnPlans = this.utilityService.sort(
        this.plan.filter((x) => x.column === column),
        (x) => x.row,
      );
      if (repeatLevel2Start) {
        tokens.push(`${repeatLevel2Start.repeat}(`);
      }
      if (repeatLevel1Start) {
        tokens.push(`${repeatLevel1Start.repeat}(`);
      }
      for (const columnPlan of columnPlans) {
        tokens.push(`${columnPlan.extra ?? columnPlan.number}${this.getRowLetter(columnPlan.row)}`);
      }
      if (repeatLevel1End) {
        tokens.push(')');
      }
      if (repeatLevel2End) {
        tokens.push(')');
      }
    }

    this.setCode(tokens.join(''));
  }

  // A draft/peg/sley plan is a sparse grid of marks, so its code lists the marked
  // cells as <row>X<col>Y<mark> run together, e.g. 1X1YX2X1YO1X2YX - the format
  // TextileMan writes, so codes stay interchangeable with the legacy app. An extras
  // cell is lettered on whichever axis it sits: a draft's extras rows read AX1YE,
  // a peg's extras columns read 1XAYE.
  private gridToCoordinateCode(): string {
    const cells = this.utilityService.sort(
      (this.plan ?? []).filter((x) => x.number),
      (x) => x.column,
      (x) => x.row,
    );
    return cells
      .map(
        (cell) => `${this.rowLabel(cell.row)}X${this.codeColumnLabel(cell.column)}Y${this.numberToMarker(cell.number)}`,
      )
      .join('');
  }

  // Peg extras columns are written as A, B, C...; every other column as its number.
  private codeColumnLabel(column: number): string {
    return column <= this.extrasColumnCount ? this.getRowLetter(column) : column.toString();
  }

  // The inverse of codeColumnLabel.
  private labelColumn(label: string): number {
    return label >= 'A' && label <= 'Z' ? this.getLetterRow(label) : +label;
  }

  private setCode(code: string) {
    this.fieldModel = { code };
    this.model[this.codeField] = code;
    this.form.patchValue({ [this.codeField]: code } as any);
  }

  private codeToGrid(code: string) {
    code = (code ?? '').toUpperCase();
    const parsed = this.coordinateCode ? this.parseCoordinateCode(code) : this.parseLetterCode(code);
    if (parsed) {
      this.form.patchValue({
        [this.key]: parsed.plans,
        [this.planRepeatsProperty]: parsed.planRepeats,
      } as any);
      this.clearGrid();
      this.populatePlans(parsed.plans);
      this.populatePlanRepeats(parsed.planRepeats);
      this.calculate();
      this.setEdited();
    }
    this.gridToCode();
  }

  // Reads a count - one or more digits, or '*' for an extra. Returns null when
  // no count is present (multi-digit counts, e.g. 12A, are supported).
  private readCount(code: string, i: number): { count: string; next: number } {
    if (code[i] === '*') {
      return { count: '*', next: i + 1 };
    }
    let count = '';
    while (i < code.length && code[i] >= '0' && code[i] <= '9') {
      count += code[i];
      i++;
    }
    return count ? { count, next: i } : null;
  }

  // The highest row the plan currently allows - the editable callback owns that
  // rule (warp: shadesPerWarp, weft: shadesPerWeft), so ask it rather than
  // reading a shade-count field that only exists on one of the models.
  private get maxEnabledRow(): number {
    if (!this.props.editable) {
      return this.rowCount;
    }
    let max = 0;
    for (let row = 1; row <= this.rowCount; row++) {
      if (this.props.editable(this.model, row)) {
        max = row;
      }
    }
    return max || this.rowCount;
  }

  // Letters mode: <count><LETTER> pairs, e.g. 4A3(2A1B). Ascending letters
  // stack into the same column; a repeated/descending letter starts a new one.
  private parseLetterCode(code: string): { plans: WarpWeftPlan[]; planRepeats: WarpWeftPlanRepeat[] } {
    const maxLetter = this.getRowLetter(this.maxEnabledRow);

    let lastLetter = 'Z';
    let column = 0;
    const plans: WarpWeftPlan[] = [];
    const planRepeats: WarpWeftPlanRepeat[] = [];
    const planRepeatsStack: WarpWeftPlanRepeat[] = [];
    let i = 0;
    while (i < code.length) {
      if (code[i] === ' ') {
        i++;
        continue;
      }
      if (code[i] === ')') {
        if (!planRepeatsStack.length) {
          return null;
        }
        planRepeatsStack[planRepeatsStack.length - 1].endColumn = column;
        planRepeats.push(planRepeatsStack.pop());
        lastLetter = 'Z';
        i++;
        continue;
      }

      const read = this.readCount(code, i);
      if (!read || read.next >= code.length) {
        return null;
      }
      i = read.next;
      const letter = code[i];
      if (letter === '(') {
        if (read.count === '*' || planRepeatsStack.length >= 2) {
          return null;
        }
        planRepeatsStack.forEach((x) => (x.level = 2));
        planRepeatsStack.push({
          startColumn: column + 1,
          level: 1,
          repeat: +read.count,
        } as WarpWeftPlanRepeat);
        i++;
        continue;
      }
      if (letter < 'A' || letter > maxLetter) {
        return null;
      }
      if (letter <= lastLetter) {
        column++;
      }
      lastLetter = letter;
      plans.push({
        column,
        row: this.getLetterRow(letter),
        number: read.count === '*' ? 0 : +read.count,
        extra: read.count === '*' ? '*' : undefined,
      });
      i++;
    }
    return planRepeatsStack.length ? null : { plans, planRepeats };
  }

  // Coordinate mode (draft/peg/sley): <row>X<col>Y<mark> cells, e.g. 1X1YX2XAYE.
  // Each cell carries its own position, so the order is presentational only and
  // there are no repeats. Whitespace is tolerated on input but never written.
  private parseCoordinateCode(code: string): { plans: WarpWeftPlan[]; planRepeats: WarpWeftPlanRepeat[] } {
    const plans: WarpWeftPlan[] = [];
    const cells = code.replace(/\s/g, '');
    // Either axis can be a letter - a draft's extras row, or a peg's extras column.
    const pattern = /([0-9]+|[A-Z])X([0-9]+|[A-Z])Y([A-Z])/g;
    // Anything the cell pattern doesn't consume is a typo - reject rather than
    // silently drop it, so the user sees the code snap back.
    if (cells.replace(pattern, '')) {
      return null;
    }
    pattern.lastIndex = 0;
    let match: RegExpExecArray;
    while ((match = pattern.exec(cells)) !== null) {
      const row = this.labelRow(match[1]);
      const column = this.labelColumn(match[2]);
      const number = this.markerToNumber(match[3]);
      if (!column || column > this.columnCount || !row || row > this.rowCount || !number) {
        return null;
      }
      // A coordinate cell is always a mark, never a warp 'extra' ('*').
      plans.push({ column, row, number, extra: undefined });
    }
    return { plans, planRepeats: [] };
  }
}
